/**
 * AgroTech — Remediation Missions  (Step 8 of the 9-step intelligence loop)
 *
 * Provides:
 *  1. Remediation type selection (Spray / Fertilise / Marker / Ground Team)
 *  2. Target precision zone definition from drone-confirmed footprint
 *  3. Dynamic chemical payload calculation (product × area × application rate)
 *  4. Mission briefing checklist & dispatch
 *  5. Post-remediation rescan scheduler (closing the loop back to Step 2)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Droplets, FlaskConical, MapPin, Users, ShieldAlert,
  RefreshCw, ChevronDown, ChevronUp, CheckCircle2,
  Activity, ExternalLink, Calendar, Calculator, Sparkles,
  ArrowRight, ShieldCheck, Check, Download
} from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import Alert from '../../components/common/Alert';
import { useNavigate } from 'react-router-dom';
import api, { missionsApi, agrotechDroneApi, reportsApi } from '../../services/api';
import { useOrgSettings } from '../../hooks/useOrgSettings';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SurveyMission {
  id: string;
  name: string;
  metadata?: {
    anomaly_id?: string;
    boundary_name?: string;
    anomaly_type?: string;
    diagnosis?: string;
    step7_status?: string;
    affected_area_ha?: number;
    affected_area_hectares?: number;
    drone_analysis?: {
      diagnosis: string;
      category: string;
      severity: string;
      affected_area_ha: number;
      recommended_actions: Array<{ action: string; priority: string; remediation_type: string }>;
    };
    [key: string]: any;
  };
}

interface RemediationPlan {
  remediation_type: string;
  target_zone_ha: number;
  recommended_drone: string;
  spray_altitude_m: number;
  no_spray_buffer_m: number;
  chemical_spec: {
    chemical: string;
    rate_kg_per_ha: number;
    water_rate_l_per_ha: number;
    total_chemical_kg: number;
    total_volume_l: number;
    max_allowable_dosage_kg_ha: number;
  };
  flight_estimate: {
    flight_time_min: number;
    nozzle_spacing_m: number;
    spray_width_m: number;
    flight_speed_ms: number;
  };
  rescan_scheduled_days: number;
}

export const RemediationMissionsPage: React.FC = () => {
  const navigate = useNavigate();
  const { uom } = useOrgSettings();
  const [missions, setMissions]               = useState<SurveyMission[]>([]);
  const [selectedSurvey, setSelectedSurvey]   = useState<SurveyMission | null>(null);
  const [remediationType, setRemediationType] = useState<string>('precision_spray');
  const [plan, setPlan]                       = useState<RemediationPlan | null>(null);
  const [loading, setLoading]                 = useState(true);
  const [dispatching, setDispatching]         = useState(false);
  const [error, setError]                     = useState<string | null>(null);
  const [success, setSuccess]                 = useState<string | null>(null);

  // Dosage customisation state
  const [rateOverride, setRateOverride]       = useState<number | null>(null);
  const [rescanDays, setRescanDays]           = useState<number>(14);

  const loadConfirmedSurveys = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/missions', { params: { limit: 100 } });
      const all: SurveyMission[] = res.data?.data?.missions ?? res.data?.missions ?? [];
      const confirmed = all.filter(
        (m) => m.metadata?.step7_status === 'confirmed' || m.metadata?.ai_analysis_status === 'complete'
      );
      setMissions(confirmed);
      if (confirmed.length > 0 && !selectedSurvey) {
        setSelectedSurvey(confirmed[0]);
      }
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to load survey missions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfirmedSurveys();
  }, [loadConfirmedSurveys]);

  // Recalculate remediation plan when survey or type changes
  useEffect(() => {
    if (!selectedSurvey) {
      setPlan(null);
      return;
    }

    agrotechDroneApi.getRemediationPlan(selectedSurvey.id, remediationType)
      .then((res) => {
        if (res?.data?.plan) {
          setPlan(res.data.plan);
          setRateOverride(res.data.plan.chemical_spec.rate_kg_per_ha);
          setRescanDays(res.data.plan.rescan_scheduled_days);
        }
      })
      .catch(() => {
        // client fallback
        const area = Number(selectedSurvey.metadata?.affected_area_ha || selectedSurvey.metadata?.affected_area_hectares || 1.8);
        const rate = remediationType === 'precision_fertiliser' ? 2.5 : remediationType === 'marker_flight' ? 0.1 : 0.5;
        setPlan({
          remediation_type: remediationType,
          target_zone_ha: area,
          recommended_drone: 'DJI Agras T40 / T50 (Sprayer)',
          spray_altitude_m: 3.5,
          no_spray_buffer_m: 10,
          chemical_spec: {
            chemical: remediationType === 'precision_fertiliser' ? 'Foliar N-P-K (12-4-6)' : 'Azoxystrobin 250 SC',
            rate_kg_per_ha: rate,
            water_rate_l_per_ha: 20,
            total_chemical_kg: Number((rate * area).toFixed(2)),
            total_volume_l: Number((20 * area).toFixed(1)),
            max_allowable_dosage_kg_ha: 1.5,
          },
          flight_estimate: {
            flight_time_min: Math.max(10, Math.round(area * 6)),
            nozzle_spacing_m: 0.5,
            spray_width_m: 6.0,
            flight_speed_ms: 5.5,
          },
          rescan_scheduled_days: 14,
        });
        setRateOverride(rate);
      });
  }, [selectedSurvey?.id, remediationType]);

  const handleDispatchRemediation = async () => {
    if (!selectedSurvey || !plan) return;
    setDispatching(true);
    setError(null);
    try {
      const activeRate = rateOverride ?? plan.chemical_spec.rate_kg_per_ha;
      const totalChem = Number((activeRate * plan.target_zone_ha).toFixed(2));
      const totalVol = Number((plan.chemical_spec.water_rate_l_per_ha * plan.target_zone_ha).toFixed(1));

      const res = await missionsApi.create({
        name: `Remediation — ${selectedSurvey.metadata?.boundary_name || 'Field'} (${plan.remediation_type.replace(/_/g, ' ')})`,
        description: [
          `Step 8 Targeted Remediation Mission.`,
          `Diagnosis: ${selectedSurvey.metadata?.drone_analysis?.diagnosis || selectedSurvey.metadata?.diagnosis || 'Confirmed stress'}`,
          `Target Zone: ${plan.target_zone_ha} ha · Buffer: ${plan.no_spray_buffer_m}m from waterways`,
          `Product: ${plan.chemical_spec.chemical} at ${activeRate} kg/ha (Total: ${totalChem} kg in ${totalVol} L)`,
          `Rescan scheduled in ${rescanDays} days to verify efficacy.`,
        ].join('\n'),
        status: 'planned',
        mission_type: 'remediation',
        metadata: {
          step: 8,
          survey_mission_id: selectedSurvey.id,
          anomaly_id: selectedSurvey.metadata?.anomaly_id,
          boundary_name: selectedSurvey.metadata?.boundary_name,
          remediation_type: plan.remediation_type,
          target_zone_ha: plan.target_zone_ha,
          spray_1: {
            chemical: plan.chemical_spec.chemical,
            rate_kg_per_ha: activeRate,
            total_volume_l: totalVol,
            area_ha: plan.target_zone_ha,
            buffer_m: plan.no_spray_buffer_m,
          },
          recommended_drone: plan.recommended_drone,
          rescan_scheduled_days: rescanDays,
          rescan_due_date: new Date(Date.now() + rescanDays * 86400000).toISOString().slice(0, 10),
        },
      });

      const newId = res?.data?.mission?.id ?? res?.mission?.id ?? res?.id;
      setSuccess(`Remediation Mission dispatched successfully${newId ? ` (ID: ${newId.slice(0, 8)}…)` : ''}! Verification re-survey scheduled.`);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to dispatch remediation mission');
    } finally {
      setDispatching(false);
    }
  };

  const calculatedTotalChem = plan && rateOverride != null
    ? Number((rateOverride * plan.target_zone_ha).toFixed(2))
    : (plan?.chemical_spec.total_chemical_kg ?? 0);

  const dosageExceeded = plan && rateOverride != null && rateOverride > plan.chemical_spec.max_allowable_dosage_kg_ha;

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Droplets className="w-7 h-7 text-cyan-600" /> Remediation Mission Planning
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Step 8 — Target precision spray/treatment zones, calculate chemical payloads, and schedule Step 8.6 verification rescans
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadConfirmedSurveys} className="flex items-center gap-1.5">
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      {error && <Alert type="error" onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert type="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      {missions.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <ShieldAlert className="mx-auto w-12 h-12 text-gray-400 mb-3" />
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">No Confirmed Diagnoses Ready for Remediation</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 max-w-sm mx-auto mt-1 mb-4">
              Remediation plans require a signed-off diagnosis from Step 7 — Analysis Review.
            </p>
            <Button variant="primary" onClick={() => navigate('/agrotech/analysis-review')}>
              Go to Step 7 — Analysis Review →
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* ── Left: Confirmed Survey Selection ── */}
          <div className="space-y-3">
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Confirmed Survey Diagnoses ({missions.length})
            </p>
            {missions.map((m) => {
              const isSelected = selectedSurvey?.id === m.id;
              const area = m.metadata?.drone_analysis?.affected_area_ha || m.metadata?.affected_area_ha || 1.8;
              return (
                <button
                  key={m.id}
                  onClick={() => setSelectedSurvey(m)}
                  className={[
                    'w-full text-left p-4 rounded-xl border-2 transition-all focus:outline-none focus:ring-2 focus:ring-cyan-500',
                    isSelected
                      ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-900/10'
                      : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-cyan-300',
                  ].join(' ')}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white leading-snug">{m.name}</p>
                    <span className="text-[10px] px-2 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/30 font-bold uppercase">
                      Confirmed
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-300 mb-1">
                    {m.metadata?.drone_analysis?.diagnosis || m.metadata?.diagnosis || 'Agronomic stress'}
                  </p>
                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <span>Target Zone: {area} ha</span>
                    <span className="text-cyan-600 dark:text-cyan-400 font-medium">Ready to Plan</span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* ── Right: Remediation Configuration & Payload Calculations ── */}
          <div className="lg:col-span-2 space-y-5">
            {selectedSurvey && plan && (
              <>
                {/* ── Intervention Type Selector ── */}
                <Card>
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-3">
                    1. Select Remediation Intervention Type
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { id: 'precision_spray', label: 'Precision Spray', icon: Droplets, desc: 'Targeted fungicide/herbicide' },
                      { id: 'precision_fertiliser', label: 'Fertiliser', icon: FlaskConical, desc: 'Foliar N-P-K nutrient' },
                      { id: 'marker_flight', label: 'Marker Flight', icon: MapPin, desc: 'Bio-dye marking for scouts' },
                      { id: 'ground_team', label: 'Ground Team', icon: Users, desc: 'Manual aeration / ditching' },
                    ].map((item) => {
                      const Icon = item.icon;
                      const active = remediationType === item.id;
                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => setRemediationType(item.id)}
                          className={`p-3 rounded-xl border-2 text-left transition-all ${
                            active
                              ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-950/40 text-cyan-900 dark:text-cyan-200 ring-2 ring-cyan-500/20'
                              : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:border-cyan-300'
                          }`}
                        >
                          <Icon className={`w-5 h-5 mb-1.5 ${active ? 'text-cyan-600' : 'text-gray-400'}`} />
                          <p className="font-bold text-xs">{item.label}</p>
                          <p className="text-[10px] text-gray-400 mt-0.5">{item.desc}</p>
                        </button>
                      );
                    })}
                  </div>
                </Card>

                {/* ── Payload & Chemical Calculator ── */}
                <Card>
                  <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100 dark:border-gray-800">
                    <div className="flex items-center gap-2">
                      <Calculator className="w-5 h-5 text-cyan-600" />
                      <h3 className="text-base font-bold text-gray-900 dark:text-white">
                        2. Precision Payload & Dosage Specification
                      </h3>
                    </div>
                    <span className="text-xs text-gray-400">Target Area: {plan.target_zone_ha} {uom.area_unit_label}</span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs mb-4">
                    <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <span className="text-gray-400 block mb-1">Recommended Drone Platform</span>
                        <span className="font-bold text-gray-900 dark:text-white text-sm">{plan.recommended_drone}</span>
                      </div>
                      <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <label className="text-gray-400 block mb-1 text-xs">Spray Altitude (m AGL)</label>
                        <input
                          type="number" min={1.5} max={8} step={0.5}
                          value={plan.spray_altitude_m}
                          onChange={(e) => {
                            const v = parseFloat(e.target.value) || plan.spray_altitude_m;
                            setPlan((p) => p ? { ...p, spray_altitude_m: v } : p);
                          }}
                          className="w-full text-xs p-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white font-bold"
                        />
                      </div>
                      <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <label className="text-gray-400 block mb-1 text-xs">No-Spray Buffer (m)</label>
                        <input
                          type="number" min={5} max={50} step={1}
                          value={plan.no_spray_buffer_m}
                          onChange={(e) => {
                            const v = parseFloat(e.target.value) || plan.no_spray_buffer_m;
                            setPlan((p) => p ? { ...p, no_spray_buffer_m: v } : p);
                          }}
                          className="w-full text-xs p-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white font-bold"
                        />
                      </div>
                  </div>

                  {/* Chemical Dosage Inputs */}
                  <div className="space-y-4 p-4 bg-cyan-50/50 dark:bg-cyan-950/20 border border-cyan-100 dark:border-cyan-900 rounded-xl">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="font-bold text-sm text-cyan-950 dark:text-cyan-200">
                        Active Formulation: {plan.chemical_spec.chemical}
                      </span>
                      <span className="text-xs text-cyan-700 dark:text-cyan-400">
                        Max Compliance Limit: {plan.chemical_spec.max_allowable_dosage_kg_ha} kg/ha
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                          Application Rate (kg/ha)
                        </label>
                        <input
                          type="number"
                          step="0.05"
                          min="0"
                          value={rateOverride ?? plan.chemical_spec.rate_kg_per_ha}
                          onChange={(e) => setRateOverride(parseFloat(e.target.value) || 0)}
                          className="w-full text-xs p-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white font-bold"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                          Computed Product Mass
                        </label>
                        <div className="p-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 font-bold text-cyan-600">
                          {calculatedTotalChem} kg
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                          Total Tank Water Volume
                        </label>
                        <div className="p-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 font-bold text-gray-900 dark:text-white">
                          {plan.chemical_spec.total_volume_l} Litres
                        </div>
                      </div>
                    </div>

                    {/* Flight estimate overrides */}
                    <div className="mt-2 grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                          Flight Speed (m/s)
                        </label>
                        <input
                          type="number" step="0.5" min="2" max="10"
                          value={plan.flight_estimate.flight_speed_ms}
                          onChange={(e) => {
                            const v = parseFloat(e.target.value) || plan.flight_estimate.flight_speed_ms;
                            setPlan((p) => p ? { ...p, flight_estimate: { ...p.flight_estimate, flight_speed_ms: v } } : p);
                          }}
                          className="w-full text-xs p-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white font-bold"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                          Nozzle Spacing (m)
                        </label>
                        <input
                          type="number" step="0.1" min="0.2" max="1.5"
                          value={plan.flight_estimate.nozzle_spacing_m}
                          onChange={(e) => {
                            const v = parseFloat(e.target.value) || plan.flight_estimate.nozzle_spacing_m;
                            setPlan((p) => p ? { ...p, flight_estimate: { ...p.flight_estimate, nozzle_spacing_m: v } } : p);
                          }}
                          className="w-full text-xs p-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white font-bold"
                        />
                      </div>
                    </div>

                    {dosageExceeded && (
                      <div className="p-2.5 bg-red-100 dark:bg-red-950/40 border border-red-300 text-red-800 dark:text-red-300 rounded text-xs flex items-center gap-2">
                        <ShieldAlert className="w-4 h-4 flex-shrink-0" />
                        <strong>Compliance Violation:</strong> Application rate exceeds maximum allowable limit of {plan.chemical_spec.max_allowable_dosage_kg_ha} kg/ha. Dispatch is blocked.
                      </div>
                    )}
                  </div>
                </Card>

                {/* ── Rescan Scheduler & Dispatch ── */}
                <Card>
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar className="w-5 h-5 text-cyan-600" />
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                      3. Verification Re-Survey (Step 8.6 Loop Closure)
                    </h3>
                  </div>

                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                    The platform automatically schedules a drone re-survey flight post-treatment to compute the <strong>Treatment Efficacy Score</strong> and verify NDVI recovery.
                  </p>

                  <div className="flex items-center gap-4 text-xs mb-6">
                    <span className="font-semibold text-gray-700 dark:text-gray-300">Schedule re-survey in:</span>
                    {[7, 14, 21, 28].map((days) => (
                      <button
                        key={days}
                        type="button"
                        onClick={() => setRescanDays(days)}
                        className={`px-3 py-1.5 rounded-lg border text-xs font-semibold ${
                          rescanDays === days
                            ? 'bg-cyan-600 text-white border-cyan-600'
                            : 'bg-white dark:bg-gray-800 border-gray-300 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {days} Days
                      </button>
                    ))}
                  </div>

                  <div className="flex items-center gap-3">
                    <Button
                      variant="primary"
                      onClick={handleDispatchRemediation}
                      disabled={dispatching || !!dosageExceeded}
                      className="flex-1"
                    >
                      {dispatching ? (
                        <span className="flex items-center justify-center gap-2">
                          <Spinner size="sm" /> Dispatching Remediation Mission…
                        </span>
                      ) : (
                        'Dispatch Remediation Mission & Schedule Re-Survey'
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      type="button"
                      onClick={() => reportsApi.downloadCertifiedPdf('remediation_record', selectedSurvey.id)}
                      className="flex items-center gap-1.5"
                    >
                      <Download className="w-4 h-4" /> Remediation PDF Record
                    </Button>
                  </div>
                </Card>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RemediationMissionsPage;
// Made with Bob
